import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { usePermissions } from '../hooks/usePermissions';
import { Permission } from '../core/permissions';
import { UserRole } from '../types/user';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: Permission;
  anyPermission?: Permission[];
  requiredRole?: UserRole;
  allowedRoles?: UserRole[];
  fallback?: React.ReactNode;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  anyPermission,
  requiredRole,
  allowedRoles,
  fallback,
  redirectTo = '/dashboard'
}) => {
  const { currentUser, loading } = useAuth();
  const { loading: orgLoading } = useOrganization();
  const { hasPermission, userRole } = usePermissions();
  const location = useLocation();

  // Bounded wait for role/org to resolve on permission-gated routes. Normally
  // they settle in well under this window; if they never do (an orphaned or
  // mid-onboarding user with no role, or a stuck fetch) we stop spinning and
  // redirect rather than hang the route forever (OMN-312).
  const gatesPermission = Boolean(requiredPermission || anyPermission);
  const permissionUnresolved = gatesPermission && (!userRole || orgLoading);
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  useEffect(() => {
    if (!permissionUnresolved) {
      setWaitedTooLong(false);
      return;
    }
    const id = setTimeout(() => setWaitedTooLong(true), 5000);
    return () => clearTimeout(id);
  }, [permissionUnresolved]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements
  if (requiredRole && userRole !== requiredRole) {
    return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
  }

  // Check allowed roles
  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
  }

  // Permission checks need BOTH the user's role and the organization type
  // resolved — hasPermission() returns false when either is still loading,
  // which would cause a spurious redirect on first render. Wait for the role
  // and the org fetch to settle before evaluating.
  if (requiredPermission || anyPermission) {
    if (permissionUnresolved) {
      // Stuck with no resolved role after the grace window — don't hang forever.
      if (waitedTooLong) {
        return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
      }
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (requiredPermission && !hasPermission(requiredPermission)) {
      return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
    }

    if (anyPermission && !anyPermission.some(p => hasPermission(p))) {
      return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
    }
  }

  return <>{children}</>;
};

// Convenience component for permission-based rendering
interface PermissionGuardProps {
  children: React.ReactNode;
  permission: Permission;
  fallback?: React.ReactNode;
  requireAll?: boolean; // If multiple permissions, require all or any
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  fallback = null,
  requireAll = true
}) => {
  const { hasPermission } = usePermissions();

  // Handle multiple permissions
  const permissions = Array.isArray(permission) ? permission : [permission];
  
  const hasRequiredPermissions = requireAll
    ? permissions.every(perm => hasPermission(perm))
    : permissions.some(perm => hasPermission(perm));

  if (!hasRequiredPermissions) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Convenience component for role-based rendering
interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  fallback?: React.ReactNode;
  requireAll?: boolean;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles,
  fallback = null,
  requireAll = false
}) => {
  const { userRole } = usePermissions();

  if (!userRole) {
    return <>{fallback}</>;
  }

  const hasRequiredRole = requireAll
    ? allowedRoles.every(role => userRole === role)
    : allowedRoles.includes(userRole);

  if (!hasRequiredRole) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Higher-order component for protecting components
export function withPermission<T extends object>(
  Component: React.ComponentType<T>,
  requiredPermission: Permission,
  fallback?: React.ReactNode
) {
  return function PermissionWrappedComponent(props: T) {
    return (
      <PermissionGuard permission={requiredPermission} fallback={fallback}>
        <Component {...props} />
      </PermissionGuard>
    );
  };
}

// Higher-order component for protecting components by role
export function withRole<T extends object>(
  Component: React.ComponentType<T>,
  allowedRoles: UserRole[],
  fallback?: React.ReactNode
) {
  return function RoleWrappedComponent(props: T) {
    return (
      <RoleGuard allowedRoles={allowedRoles} fallback={fallback}>
        <Component {...props} />
      </RoleGuard>
    );
  };
}